import './perks-music.component.scss';

import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { IconGrid } from '../../../common/icons/icon-grid';
import { IconList } from '../../../common/icons/icon-list';
import { t } from '../../../i18n';
import { getAlbumId, getArtworkUrl, getMainArtists } from '../../../music/models/album.helpers';
import type { Album } from '../../../music/models/album.interface';
import { setCurrentMusicAlbum } from '../../../music/store/music.reducer';
import {
  selectCurrentMusicAlbumId,
  selectMusicIsPlaying,
  selectQueueSource,
} from '../../../music/store/music.selectors';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { linkify } from '../../../utils/a11y.utils';
import { ContentType } from '../../model/tab.interface';
import {
  selectAlbums,
  selectPerksLoading,
  selectTabContentLast,
  selectTabContentNextCursor,
} from '../../store/perks.selectors';
import { loadTabContent } from '../../store/perks.thunk';
import { usePerksConfig } from '../perks/perks-config.context';

const VIEW_MODE_STORAGE_KEY = 'sng_music_view_mode';

type MusicViewMode = 'grid' | 'list';

export const PerksMusicComponent = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectPerksLoading);
  const albums = useAppSelector(selectAlbums);
  const isLast = useAppSelector(selectTabContentLast(ContentType.ALBUM));
  const nextCursor = useAppSelector(selectTabContentNextCursor(ContentType.ALBUM));
  const currentAlbumId = useAppSelector(selectCurrentMusicAlbumId);
  const isPlaying = useAppSelector(selectMusicIsPlaying);
  const queueSource = useAppSelector(selectQueueSource);
  // Only an album actually playing should pin the current album; a playing
  // post must not block selecting one (SNGL-7299)
  const isAlbumPlaying = isPlaying && queueSource === 'album';
  const { searchQuery, columns } = usePerksConfig();
  const [, setLocation] = useHashLocation();
  const [viewMode, setViewMode] = useState<MusicViewMode | null>(() => {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === 'grid' || stored === 'list' ? stored : null;
  });

  // The list styles are scoped to the mobile media query, so desktop always
  // renders the grid regardless of this class. With a single album a one-row
  // list is pointless: force the grid and hide the toggle.
  const showViewToggle = (albums?.length ?? 0) > 1;
  const activeViewMode: MusicViewMode = showViewToggle ? (viewMode ?? 'list') : 'grid';

  const handleViewModeChange = (mode: MusicViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  };

  const onlyAlbumId = albums?.length === 1 && isLast ? getAlbumId(albums[0]) : null;

  useEffect(() => {
    if (!onlyAlbumId) return;
    if (!isAlbumPlaying) {
      dispatch(setCurrentMusicAlbum(onlyAlbumId));
    }
    setLocation(`/music/${onlyAlbumId}`, { replace: true });
  }, [onlyAlbumId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const cssVars = {
    '--productColumns': columns,
  };

  const filteredAlbums = useMemo(() => {
    if (!albums) return [];
    if (!searchQuery) return albums;

    const query = searchQuery.toLowerCase();
    return albums.filter((item: Album) => item?.title?.toLowerCase().includes(query));
  }, [albums, searchQuery]);

  const currentAlbum = useMemo(() => {
    if (!albums) return null;
    return albums.find((a: Album) => getAlbumId(a) === currentAlbumId) ?? albums[0] ?? null;
  }, [albums, currentAlbumId]);

  const handleLoadMore = () => {
    dispatch(
      loadTabContent({ contentType: ContentType.ALBUM, cursor: nextCursor, loadMore: true })
    );
  };

  const handleAlbumClick = (album: Album) => {
    const albumId = getAlbumId(album);
    if (!albumId) return;
    if (!isAlbumPlaying) {
      dispatch(setCurrentMusicAlbum(albumId));
    }
    setLocation(`/music/${albumId}`);
  };

  if (loading && !albums?.length) {
    return (
      <section class="single-loading">
        <SpinnerComponent />
      </section>
    );
  }

  if (!albums) return null;

  if (onlyAlbumId) {
    return (
      <section class="single-loading">
        <SpinnerComponent />
      </section>
    );
  }

  return (
    <div class={`single-perks-music-container single-music-view-${activeViewMode}`} style={cssVars}>
      {showViewToggle && (
        <div class="single-music-view-toggle">
          <div class="single-music-view-toggle-group">
            <button
              class={`single-music-view-toggle-btn ${activeViewMode === 'grid' ? 'active' : ''}`}
              aria-label={t('perks.gridView')}
              aria-pressed={activeViewMode === 'grid'}
              onClick={() => handleViewModeChange('grid')}
            >
              <IconGrid width={16} height={16} />
            </button>
            <button
              class={`single-music-view-toggle-btn ${activeViewMode === 'list' ? 'active' : ''}`}
              aria-label={t('perks.listView')}
              aria-pressed={activeViewMode === 'list'}
              onClick={() => handleViewModeChange('list')}
            >
              <IconList width={16} height={16} />
            </button>
          </div>
        </div>
      )}
      {filteredAlbums.length > 0 ? (
        <>
          <section class="single-card-grid">
            {filteredAlbums.map((album: Album) => {
              const albumId = getAlbumId(album);
              const artworkUrl = getArtworkUrl(album.images);
              const primaryArtist = getMainArtists(album.parties)[0];
              return (
                <div
                  key={albumId}
                  class={`single-music-album-card ${albumId === getAlbumId(currentAlbum) ? 'active' : ''}`}
                  {...linkify(() => handleAlbumClick(album), album.title)}
                >
                  {artworkUrl && (
                    <img src={artworkUrl} alt={album.title} class="single-music-album-artwork" />
                  )}
                  <div class="single-music-album-info">
                    <h5 class="single-music-album-title h5 single-h5">{album.title}</h5>
                    <h6 class="single-music-album-artist h6 single-h6">{primaryArtist?.name}</h6>
                    <p class="single-music-album-meta">
                      {t(album.tracks.length === 1 ? 'perks.trackCountOne' : 'perks.trackCount', {
                        count: String(album.tracks.length),
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
          </section>
          {!searchQuery && !isLast && (
            <section className="single-load-more">
              <button className="button btn" onClick={handleLoadMore} disabled={loading}>
                {loading ? t('common.loading') : t('perks.loadMore')}
              </button>
            </section>
          )}
        </>
      ) : (
        <div class="no-results">
          {t('perks.noMusic')}
          {searchQuery && <span>&nbsp;{t('perks.foundFor', { query: searchQuery })}</span>}
        </div>
      )}
    </div>
  );
};
